import React from 'react';
import { Box, Link, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import Markdown, { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';

/**
 * The Assistant's markdown, drawn with the app's own components so it reads like the rest of the panel. Raw HTML in
 * the model's reply is left as text.
 */
const components: Components = {
  p: ({ children }) => (
    <Typography
      variant="body2"
      component="p"
      sx={{ my: 0.75, '&:first-of-type': { mt: 0 }, '&:last-child': { mb: 0 } }}
    >
      {children}
    </Typography>
  ),
  h1: ({ children }) => (
    <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 1.5, mb: 0.5 }}>
      {children}
    </Typography>
  ),
  h2: ({ children }) => (
    <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 1.5, mb: 0.5 }}>
      {children}
    </Typography>
  ),
  h3: ({ children }) => (
    <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 1.25, mb: 0.5 }}>
      {children}
    </Typography>
  ),
  h4: ({ children }) => (
    <Typography variant="subtitle2" sx={{ mt: 1, mb: 0.5 }}>
      {children}
    </Typography>
  ),
  ul: ({ children }) => (
    <Box component="ul" sx={{ my: 0.75, pl: 2.5, typography: 'body2' }}>
      {children}
    </Box>
  ),
  ol: ({ children }) => (
    <Box component="ol" sx={{ my: 0.75, pl: 2.5, typography: 'body2' }}>
      {children}
    </Box>
  ),
  li: ({ children }) => (
    <Box component="li" sx={{ my: 0.25 }}>
      {children}
    </Box>
  ),
  a: ({ href, children }) => (
    <Link href={href} target="_blank" rel="noopener noreferrer">
      {children}
    </Link>
  ),
  blockquote: ({ children }) => (
    <Box
      component="blockquote"
      sx={{ m: 0, my: 0.75, pl: 1.5, borderLeft: 3, borderColor: 'divider', color: 'text.secondary' }}
    >
      {children}
    </Box>
  ),
  hr: () => <Box component="hr" sx={{ border: 0, borderTop: 1, borderColor: 'divider', my: 1.5 }} />,
  code: ({ className, children }) => {
    // Fenced blocks carry a language class or span lines; everything else is inline code.
    const isBlock = /language-/.test(className ?? '') || String(children).includes('\n');
    return isBlock ? (
      <Box
        component="pre"
        sx={{
          my: 0.75,
          p: 1,
          overflowX: 'auto',
          bgcolor: 'action.hover',
          borderRadius: 1,
          fontFamily: 'monospace',
          fontSize: '0.8rem',
        }}
      >
        <code>{children}</code>
      </Box>
    ) : (
      <Box
        component="code"
        sx={{
          px: 0.5,
          py: 0.125,
          bgcolor: 'action.hover',
          borderRadius: 0.5,
          fontFamily: 'monospace',
          fontSize: '0.85em',
        }}
      >
        {children}
      </Box>
    );
  },
  pre: ({ children }) => <>{children}</>,
  table: ({ children }) => (
    <TableContainer sx={{ my: 1, border: 1, borderColor: 'divider', borderRadius: 1, overflowX: 'auto' }}>
      <Table size="small">{children}</Table>
    </TableContainer>
  ),
  thead: ({ children }) => <TableHead sx={{ bgcolor: 'action.hover' }}>{children}</TableHead>,
  tbody: ({ children }) => <TableBody>{children}</TableBody>,
  tr: ({ children }) => <TableRow sx={{ '&:last-child td': { borderBottom: 0 } }}>{children}</TableRow>,
  th: ({ children, style }) => (
    <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap', py: 0.5, px: 1 }} style={style}>
      {children}
    </TableCell>
  ),
  td: ({ children, style }) => (
    <TableCell sx={{ py: 0.5, px: 1 }} style={style}>
      {children}
    </TableCell>
  ),
};

export function AssistantMarkdown({ text }: { text: string }) {
  return (
    <Box sx={{ wordBreak: 'break-word' }}>
      <Markdown remarkPlugins={[remarkGfm]} components={components}>
        {text}
      </Markdown>
    </Box>
  );
}
